import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '@/services/dashboardService';
import { PageLoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useTenant } from '@/contexts/TenantContext';

/**
 * DefaultDashboardRedirect
 *
 * Redirects users to the default dashboard or first available dashboard.
 * If no dashboards exist, shows a prompt to create one.
 */
export function DefaultDashboardRedirect() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [noDashboards, setNoDashboards] = useState(false);
  const { currentTenant, isLoading: tenantLoading } = useTenant();

  useEffect(() => {
    // Wait for tenant context to initialize
    if (tenantLoading || !currentTenant) {
      return;
    }

    const redirectToDefaultDashboard = async () => {
      try {
        const defaultDashboard = await DashboardService.getDefaultDashboard();

        if (defaultDashboard) {
          // Redirect to the default dashboard
          navigate(`/app/dashboards/${defaultDashboard.id}`, { replace: true });
        } else {
          // No default dashboard set - check if any dashboards exist
          const allDashboards = await DashboardService.getDashboards();

          if (allDashboards.length > 0) {
            // Redirect to the first available dashboard
            navigate(`/app/dashboards/${allDashboards[0].id}`, { replace: true });
          } else {
            // Truly no dashboards exist
            setNoDashboards(true);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error('Failed to fetch default dashboard:', error);
        setNoDashboards(true);
        setLoading(false);
      }
    };

    redirectToDefaultDashboard();
  }, [navigate, tenantLoading, currentTenant]);

  if (loading || tenantLoading) {
    return <PageLoadingSpinner message="Loading dashboard..." />;
  }

  if (noDashboards) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-foreground">No Dashboards Found</h2>
          <p className="text-muted-foreground max-w-md">
            Get started by creating your first dashboard. You can add widgets to track
            projects, appointments, tasks, and more.
          </p>
        </div>
        <Button
          onClick={() => navigate('/app/dashboards/new')}
          size="lg"
          className="gap-2"
        >
          <Plus className="h-5 w-5" />
          Create Your First Dashboard
        </Button>
      </div>
    );
  }

  return null;
}
